import { AfterViewInit, Component, Injector } from '@angular/core';
import { ComponentDataService } from '../../../services/component-data.service';

import {
  ButtonsContainerComponent,
  DropdownItem,
  FormHeaderComponent,
  QformAttachmentRequestEvent,
  QformEvent,
  QformEventBus,
} from 'qflow-form-renderer';
import { LocalizePipe } from '../../../core/pipes/localization.pipe';
import {
  AttachmentDataInputDto,
  AttachmentsClient,
  DataDomainClient,
  FlowClient,
  FlowRoleInputDto,
  GuestTaskRenderInfoOutputDto,
  GuidStringKeyValuePair,
  RespondTaskAsGuestInputDto,
  RespondTaskInputDto,
  RoleClient,
  SessionClient,
  TaskClient,
  TaskRenderInfoOutputDto,
  TaskResponseOutputDto,
  TaskResponseResult,
} from '../../../core/remote-clients/remote-client';
import { RedirectService } from '../../../core/services/redirect.service';
import { SessionService } from '../../../core/services/session.service';
import { AttachmentService } from '../../../services/attachments/attachment.service';
import { ConfigurationService } from '../../../services/configuration.service.ts/configuration.service';
import { RemoteServiceBase } from '../../../services/remote/remote-base.service';
import { RemoteDataDomainService } from '../../../services/remote/remote-data-domain.service';
import {
  RemoteFlowService,
  TaskRenderInfoInputDto,
} from '../../../services/remote/remote-flow.service';
import { RemoteRoleService } from '../../../services/remote/remote-role.service';
import { FormRenderComponent } from '../form-render/form-render.component';
import { InteractiveStepBaseComponent } from '../interactive-step-base/interactive-step-base.component';

@Component({
  selector: 'app-task-form',
  imports: [
    LocalizePipe,
    FormRenderComponent,
    FormHeaderComponent,
    ButtonsContainerComponent,
  ],
  providers: [
    AttachmentsClient,
    ComponentDataService,
    AttachmentService,
    SessionClient,
    SessionService,
    RedirectService,
    RemoteServiceBase,
    RemoteFlowService,
    RemoteRoleService,
    RemoteDataDomainService,
    DataDomainClient,
    RoleClient,
    FlowClient,
    TaskClient,
  ],
  templateUrl: './task-form.component.html',
  styleUrl: './task-form.component.scss',
})
export class TaskFormComponent
  extends InteractiveStepBaseComponent
  implements AfterViewInit
{
  task: TaskRenderInfoOutputDto | undefined = undefined;
  flowId: string = '';
  taskId: string = '';
  taskToId: string = '';
  attachments?: AttachmentDataInputDto[];
  headerDropdownUrls: DropdownItem[] = [];
  guestToken: string = '';
  guestUserEmail: string = '';
  guestTimeZone: string = '';
  canRespond: boolean = true;
  currentTaskResponse: TaskResponseOutputDto | undefined = undefined;

  get isNotificationTask(): boolean {
    if (!this.task) {
      return false;
    }
    return !this.task.responsesConfiguration?.responses || 
           this.task.responsesConfiguration.responses.length === 0;
  }

  get taskTitle(): string {
    return this.isNotificationTask 
      ? this._localizationService.localize('Notification')
      : this._localizationService.localize('RespondTask');
  }

  get taskSubtitle(): string {
    if (!this.task) {
      return '';
    }
    
    if (this.isNotificationTask) {
      const subjectLabel = this._localizationService.localize('NotificationSubject');
      return `${subjectLabel}: ${this.task.subject ?? ''}`;
    } else {
      return this.task.taskName ?? '';
    }
  }

  constructor(
    private readonly _attachmentService: AttachmentService,
    private readonly _config: ConfigurationService,
    injector: Injector
  ) {
    super(injector);

    if (this.isGuest) {
      this.guestToken = this._route.snapshot.queryParamMap.get('token') ?? '';
    }
    this.flowId = this._route.snapshot.queryParamMap.get('flowId') ?? '';
    this.taskId = this._route.snapshot.queryParamMap.get('taskId') ?? '';
    this.taskToId = this._route.snapshot.queryParamMap.get('taskToId') ?? '';

    const eventBus = injector.get(QformEventBus);

    eventBus.listen(
      QformEvent.AttachmentDownloadRequested,
      async (args: QformAttachmentRequestEvent) => {
        this._attachmentService.downloadFlowAttachment(
          args.attachId,
          args.version
        );
      }
    );

    eventBus.listen(
      QformEvent.AttachmentDeletionRequested,
      async (args: QformAttachmentRequestEvent) => {
        this._attachmentService.deleteFlowAttachment(
          this.flowId,
          this.taskId,
          this.taskToId,
          args.attachId,
          args.version
        );
      }
    );
  }

  protected override updateTitle(childTitle: string) {
    let localizedTitle: string;
    
    if (this.isNotificationTask) {
      localizedTitle = this._localizationService.localize('Notification');
    } else {
      localizedTitle = this._localizationService.localize(
        'RespondTaskTitle',
        childTitle
      );
    }

    super.updateTitle(localizedTitle);
  }

  async ngAfterViewInit(): Promise<void> {
    this.showLoader();

    if (this.isGuest) {
      await this.loadGuestTaskRenderInfo();
    } else {
      await this.loadTaskRenderInfo();
      this.setHeaderUrls();
    }
    this.hideLoader();
  }

  private async loadTaskRenderInfo(): Promise<void> {
    try {
      if (!this.areValidTaskRenderInfoParams()) {
        this.redirectToErrorPage(
          this._localizationService.localize('TaskNotFound')
        );
        return;
      }

      const renderInfoInput: TaskRenderInfoInputDto = {
        flowId: this.flowId,
        taskId: this.taskId,
        taskToId: this.taskToId,
        tenantId: this.tenantId,
      };
      const result = (await this._flowService.getTaskRenderInfo(
        renderInfoInput
      )) as TaskRenderInfoOutputDto;

      await this.processValidTaskRenderInfo(result);
    } catch {
      this.redirectToErrorPage(
        this._localizationService.localize('UnexpectedErrorLoadingTask')
      );
    } finally {
      this.hideLoader();
    }
  }

  private areValidTaskRenderInfoParams(): boolean {
    return (
      this.isValidGuid(this.flowId) &&
      this.isValidGuid(this.taskId) &&
      this.isValidGuid(this.taskToId)
    );
  }

  private async loadGuestTaskRenderInfo(): Promise<void> {
    try {
      const renderInfo = (await this._flowService.getTaskRenderInfo({
        token: this.guestToken,
        tenantId: this.tenantId,
      })) as GuestTaskRenderInfoOutputDto;

      this.flowId = renderInfo!.flowId!;
      this.taskId = renderInfo!.taskId!;
      this.taskToId = renderInfo!.taskToId!;
      this.flowId = renderInfo!.flowId!;
      this.guestUserEmail = renderInfo!.email!;

      await this.processValidTaskRenderInfo(renderInfo);
    } catch {
      this.redirectToErrorPage(
        this._localizationService.localize('UnexpectedErrorLoadingTask')
      );
    } finally {
      this.hideLoader();
    }
  }
  private async processValidTaskRenderInfo(
    renderInfo: TaskRenderInfoOutputDto
  ): Promise<void> {
    this.task = renderInfo;
    this.versionId = renderInfo.versionId!;
    this.commentsScope = renderInfo.scope?.commentScope;
    this.attachmentsScope = renderInfo.scope?.attachmentsScope;
    this.canRespond = !renderInfo.isReadOnly!;

    const formJson = JSON.parse(this.task.customForm?.content ?? '');
    this.qformCard.parse(formJson);
    this.initDependencies(formJson, this.task.data ?? []);

    this.initData(this.task.data ?? []);
    await this.fillListDataOptions(this.task.data ?? []);
    await this.fillDataControlWithValueAndScope(this.task.data ?? []);
    await this.fillDependentListDataOptions(this.task.data ?? []);
    this.guestTimeZone = this._cultureService.currentTimeZone;
    this.fillFileDataWithScope();
    this.fillRolesWithScope(this.task.roles ?? []);

    if (this.qformCard.getCommentLog()) {
      this.initCommentLog(renderInfo.comments!);
    }

    this.initFlowInfo({
      correlativeId: renderInfo.flowCorrelativeId,
      name: renderInfo.flowName,
      description: renderInfo.flowDescription,
      initiatorUser: renderInfo.initiatorUserName,
      startDate: renderInfo.startDate,
      status: renderInfo.status,
      importance: renderInfo.importance,
      flag: renderInfo.flag,
      progress: renderInfo.progress,
    });

    this.renderQformCard();

    if (this.task.responsesConfiguration.responseKey) {
      this.currentTaskResponse =
        this.task.responsesConfiguration.responses?.find(
          response =>
            response.key === this.task?.responsesConfiguration.responseKey
        );
    }
    this.updateTitle(this.task.subject!);
  }

  async respondTask(response: any) {
    if (!(response instanceof TaskResponseOutputDto)) {
      return;
    }

    try {
      this.validateForm(response.commentRequired ?? false);

      this.showLoader();

      await this.uploadFiles();

      const dataValues = this.getDataValues();
      const roleMembers = this.getRoleMembers();

      const respondTaskDto = this.buildRespondTaskDto(
        response.key!,
        dataValues,
        roleMembers
      );

      const result = await this._flowService.respondTask(respondTaskDto);

      this.redirectToTaskRespondedView(
        result,
        response.value!,
        this.task!.subject!
      );
    } catch (error) {
      this.handleException(error, 'ErrorRespondingTask');
      this.hideLoader();
    } finally {
      this.attachments = [];
    }
  }

  private async uploadFiles() {
    this.attachments = await this._attachmentService.uploadTempFiles(
      this.qformCard,
      this.task!.versionId!
    );
  }

  private buildRespondTaskDto(
    responseKey: string,
    dataValues: GuidStringKeyValuePair[],
    roleMembers: FlowRoleInputDto[]
  ): RespondTaskInputDto | RespondTaskAsGuestInputDto {
    const comment = this.getComment() ?? '';

    const baseDto = {
      flowId: this.flowId,
      taskId: this.taskId,
      taskToId: this.taskToId,
      responseKey: responseKey,
      dataIdsWithValues: dataValues,
      roles: roleMembers,
      attachments: this.attachments,
      comment: comment,
    };

    if (!this.isGuest) {
      return new RespondTaskInputDto(baseDto);
    }

    const guestDto = baseDto as RespondTaskAsGuestInputDto;
    guestDto.email = this.guestUserEmail;
    guestDto.token = this.guestToken;
    return guestDto;
  }

  setHeaderUrls(): void {
    this.headerDropdownUrls = [
      {
        label: this._localizationService.localize('FlowDetails'),
        url: this._config
          .get<string>('FlowDetailsRoute')
          .replace('{0}', this.flowId),
      },
      {
        label: this._localizationService.localize('ViewFlow'),
        url: this._config
          .get<string>('ViewFlowRoute')
          .replace('{0}', this.flowId),
      },
    ];
  }

  redirectToTaskRespondedView(
    result: TaskResponseResult,
    responseValue: string,
    taskSubject: string
  ) {
    if (!this.isInIframe()) {
      return;
    }

    this._redirectService.redirectToRespondendTask(
      result,
      this.versionId,
      responseValue,
      taskSubject
    );
  }

  protected override getCurrentStepId(): string | undefined {
    return this.task?.templateStepId;
  }
}
